<template>
  <div class="ast" :class="{ 'ast--open': panelOpen, 'ast--active': value.length > 0 }" ref="root">
    <button type="button" class="ast-trigger" :aria-expanded="panelOpen ? 'true' : 'false'" :aria-haspopup="'listbox'"
      @click="togglePanel">
      <span class="ast-trigger-body">
        <template v-if="value.length === 0">
          <span class="ast-trigger-placeholder">{{ placeholder }}</span>
        </template>
        <template v-else>
          <span v-for="chip in triggerChips" :key="chip.id" class="ast-trigger-chip">
            <span class="ast-trigger-chip-label" :title="chip.label">{{ chip.label }}</span>
            <span class="ast-trigger-chip-x" role="button" tabindex="-1" aria-label="Remove area"
              @click.stop="removeId(chip.id)">
              <svg viewBox="0 0 10 10" width="8" height="8" fill="none" aria-hidden="true">
                <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              </svg>
            </span>
          </span>
          <span v-if="triggerOverflow > 0" class="ast-trigger-more">+{{ triggerOverflow }}</span>
        </template>
      </span>
      <span class="ast-trigger-tail">
        <span v-if="value.length > 0" class="ast-trigger-clear" role="button" tabindex="-1"
          aria-label="Clear all selected" @click.stop="clearAll">
          <svg viewBox="0 0 10 10" width="9" height="9" fill="none" aria-hidden="true">
            <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
        </span>
        <span class="ast-trigger-caret" :class="{ 'ast-trigger-caret--open': panelOpen }" aria-hidden="true">
          <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
            <path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </span>
      </span>
    </button>

    <transition name="ast-pop">
      <div v-if="panelOpen" class="ast-panel" :class="alignClass" :style="panelStyle" role="dialog"
        aria-label="Area selector">
        <div class="ast-field">
          <div class="ast-fieldlabel">{{ resolvedSearchLabel }}</div>
          <div class="ast-inputwrap">
            <span v-for="chip in panelChips" :key="chip.id" class="ast-pchip">
              <b>{{ chip.label }}</b>
              <span class="ast-pchip-x" role="button" tabindex="-1" aria-label="Remove area" @click="removeId(chip.id)">
                <svg viewBox="0 0 10 10" width="8" height="8" fill="none" aria-hidden="true">
                  <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
                </svg>
              </span>
            </span>
            <span v-if="panelOverflow > 0" class="ast-pchip-more">+{{ panelOverflow }} more</span>
            <input ref="searchInput" type="text" class="ast-search" autocomplete="off" spellcheck="false"
              v-model="query" :placeholder="leafCount ? '' : resolvedSearchPlaceholder" @keydown.esc.prevent="close" />
          </div>
        </div>

        <div class="ast-body" :style="{ maxHeight: maxHeight + 'px' }" @wheel.stop>
          <template v-if="flatRows.length">
            <div v-for="row in flatRows" :key="row.node.id" class="ast-row" :class="[
              'ast-row--l' + Math.min(row.depth, 3),
              {
                'ast-row--sel': row.state !== 'unchecked',
                'ast-row--has-kids': row.hasKids,
                'ast-row--disabled': row.disabled,
                'ast-row--inert': row.inert,
              },
            ]" :role="row.hasKids ? 'treeitem' : 'option'"
              :aria-expanded="row.hasKids ? (row.isOpen ? 'true' : 'false') : null"
              :aria-disabled="row.disabled ? 'true' : null" @click="onRowClick(row)">
              <span class="ast-guides" aria-hidden="true">
                <span v-for="i in row.depth" :key="i" class="ast-guide"></span>
              </span>
              <span class="ast-chev" :class="{ 'ast-chev--open': row.isOpen, 'ast-chev--empty': !row.hasKids }"
                aria-hidden="true">
                <svg v-if="row.hasKids" viewBox="0 0 10 10" width="9" height="9" fill="none">
                  <path d="M3 1l5 4-5 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </span>
              <span class="ast-cb" :class="{
                'ast-cb--on': row.state === 'checked',
                'ast-cb--ind': row.state === 'indeterminate',
              }" role="checkbox"
                :aria-checked="row.state === 'checked' ? 'true' : row.state === 'indeterminate' ? 'mixed' : 'false'"
                tabindex="-1" @click.stop="onToggleSel(row.node)">
                <svg v-if="row.state === 'checked'" viewBox="0 0 12 12" width="11" height="11" fill="none"
                  aria-hidden="true">
                  <path d="M2 6.2l2.6 2.6L10 3" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
                <span v-else-if="row.state === 'indeterminate'" class="ast-cb-dash" aria-hidden="true"></span>
              </span>
              <span class="ast-name" :title="row.node.label">{{ row.node.label }}</span>
              <span v-if="row.count != null" class="ast-count">{{ row.count }}</span>
            </div>
          </template>
          <div v-else class="ast-empty">No {{ itemPlural }} match &ldquo;{{ appliedQuery }}&rdquo;</div>
        </div>

        <div class="ast-foot">
          <div class="ast-summary">
            <template v-if="leafCount > 0">
              <b>{{ leafCount }}</b><template v-if="capped"> of {{ maxLeaves }}</template>
              {{ leafCount === 1 ? itemSingular : itemPlural }} selected
            </template>
            <template v-else-if="capped">
              None selected &mdash; pick up to {{ maxLeaves }} {{ itemPlural }}
            </template>
            <template v-else>No {{ itemPlural }} selected</template>
          </div>
          <div class="ast-actions">
            <button type="button" class="ast-btn" :disabled="leafCount === 0" @click="clearAll">Clear</button>
            <button type="button" class="ast-btn ast-btn--pri" @click="close">Apply</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// How long the search box waits after the last keystroke before re-filtering.
const SEARCH_DEBOUNCE_MS = 200;

export default {
  name: "AreaSelectorTree",
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Select areas…" },
    maxHeight: { type: Number, default: 360 },
    triggerLimit: { type: Number, default: 2 },
    panelLimit: { type: Number, default: 4 },
    align: { type: String, default: "left" },
    valueConsistsOf: {
      type: String,
      default: "BRANCH_PRIORITY",
      validator: (v) => ["BRANCH_PRIORITY", "LEAF_PRIORITY", "ALL"].includes(v),
    },
    panelWidth: { type: Number, default: 380 },
    searchLabel: { type: String, default: "" },
    searchInputPlaceholder: { type: String, default: "" },
    itemSingular: { type: String, default: "item" },
    itemPlural: { type: String, default: "items" },
    defaultExpandDepth: { type: Number, default: 2 },
    // THE MOST LEAVES THIS CONTROL WILL HOLD. 0 is no cap, which is every
    // existing use of this component and the default.
    //
    // A cap makes the tree behave like a picker with room for N rather than
    // like a filter: once it is full, the leaves that are NOT selected go inert
    // and the branches above them with them, so the only clicks left are the
    // ones that free a slot. The alternative -- letting the reader tick a sixth
    // and silently dropping one -- is a control that disagrees with the chart
    // it is driving.
    maxLeaves: { type: Number, default: 0 },
    // Whether the chips in the closed TRIGGER carry their `chipPrefix`
    // ("Burnaby: Brentwood"). Off, the trigger shows the bare label and only
    // the chips inside the open panel name the parent -- for a narrow trigger
    // where the prefix would truncate every chip down to the city name.
    triggerChipPrefix: { type: Boolean, default: true },
  },
  data() {
    return {
      panelOpen: false,
      query: "",
      // What the rows are actually filtered on: `query` after the reader has
      // paused typing (SEARCH_DEBOUNCE_MS). A tree with neighbourhoods under
      // every city is hundreds of rows, and re-walking it on every keystroke
      // makes the input lag behind the typing.
      appliedQuery: "",
      openIds: new Set(),
      defaultOpenSeed: 0,
    };
  },
  computed: {
    nodeIndex() {
      const idx = Object.create(null);
      const collectLeafIds = (node, acc) => {
        if (!node.children || node.children.length === 0) {
          acc.push(node.id);
        } else {
          for (const c of node.children) collectLeafIds(c, acc);
        }
        return acc;
      };
      const walk = (nodes, parentId, depth) => {
        for (const node of nodes) {
          if (!node || node.id == null) continue;
          const hasKids = !!(node.children && node.children.length > 0);
          idx[node.id] = {
            node,
            parentId,
            depth,
            isLeaf: !hasKids,
            leafIds: collectLeafIds(node, []),
          };
          if (hasKids) walk(node.children, node.id, depth + 1);
        }
      };
      walk(this.options, null, 0);
      return idx;
    },
    leafSelected() {
      const set = new Set();
      for (const id of this.value) {
        const entry = this.nodeIndex[id];
        if (!entry) {
          set.add(id);
          continue;
        }
        for (const lid of entry.leafIds) set.add(lid);
      }
      return set;
    },
    leafCount() {
      return this.leafSelected.size;
    },
    /** Whether the cap is in force at all. */
    capped() {
      return this.maxLeaves > 0;
    },
    /** Full: nothing more can be ticked until something is unticked. */
    atCap() {
      return this.capped && this.leafSelected.size >= this.maxLeaves;
    },
    normalizedQuery() {
      return this.appliedQuery.trim().toLowerCase();
    },
    flatRows() {
      const rows = [];
      const q = this.normalizedQuery;
      const matches = (node) => {
        if (!q) return true;
        if ((node.label || "").toLowerCase().includes(q)) return true;
        if (node.children && node.children.length) {
          return node.children.some(matches);
        }
        return false;
      };
      const stateOf = (node) => {
        const entry = this.nodeIndex[node.id];
        if (!entry || entry.isLeaf) {
          return this.leafSelected.has(node.id) ? "checked" : "unchecked";
        }
        // Disabled leaves can never be selected, so exclude them from the
        // branch's checked/indeterminate calculation.
        const ids = this.enabledLeafIds(entry);
        let on = 0;
        for (const id of ids) if (this.leafSelected.has(id)) on++;
        if (on === 0) return "unchecked";
        if (on === ids.length) return "checked";
        return "indeterminate";
      };
      const walk = (nodes, depth) => {
        for (const node of nodes) {
          if (!matches(node)) continue;
          const hasKids = !!(node.children && node.children.length > 0);
          const isOpen = hasKids && (this.openIds.has(node.id) || (q && this.someChildMatches(node, q)));
          rows.push({
            node,
            depth,
            hasKids,
            isOpen,
            state: stateOf(node),
            // AT THE CAP, everything not already on is greyed. A branch is
            // greyed only when none of its leaves are on, so a reader can
            // still untick from a branch that is partly in the selection.
            // A `locked` node is greyed like a disabled one, but only as a row of
            // its own -- see isDisabled().
            disabled: !!node.disabled || !!node.locked || this.capBlocks(node),
            // A disabled branch is greyed but still openable — the reader has
            // to be able to expand it to see the values it holds. Only a
            // disabled leaf is fully inert.
            inert: (!!node.disabled || !!node.locked || this.capBlocks(node)) && !hasKids,
            count: typeof node.count === "number" ? node.count : null,
          });
          if (hasKids && isOpen) walk(node.children, depth + 1);
        }
      };
      walk(this.options, 0);
      return rows;
    },
    triggerChips() {
      return this.value.slice(0, this.triggerLimit).map((id) => ({
        id,
        label: this.triggerChipPrefix ? this.chipLabelFor(id) : this.labelFor(id),
      }));
    },
    triggerOverflow() {
      return Math.max(0, this.value.length - this.triggerLimit);
    },
    panelChips() {
      return this.value.slice(0, this.panelLimit).map((id) => ({
        id,
        label: this.chipLabelFor(id),
      }));
    },
    panelOverflow() {
      return Math.max(0, this.value.length - this.panelLimit);
    },
    alignClass() {
      return this.align === "right" ? "ast-panel--right" : "ast-panel--left";
    },
    panelStyle() {
      return this.panelWidth ? { width: this.panelWidth + "px" } : {};
    },
    resolvedSearchLabel() {
      if (this.searchLabel) return this.searchLabel;
      const base = (this.placeholder || "").replace(/[.…]+$/g, "").trim();
      return base ? `Search ${base}` : "Search";
    },
    resolvedSearchPlaceholder() {
      if (this.searchInputPlaceholder) return this.searchInputPlaceholder;
      const base = (this.placeholder || "").replace(/[.…]+$/g, "").trim();
      return base ? `Search ${base.toLowerCase()}…` : "Search…";
    },
  },
  watch: {
    panelOpen(open) {
      if (open) {
        this.seedDefaultOpen();
        this.$nextTick(() => {
          document.addEventListener("mousedown", this.onDocClick, true);
          document.addEventListener("keydown", this.onDocKey, true);
          if (this.$refs.searchInput) {
            try { this.$refs.searchInput.focus({ preventScroll: true }); } catch (e) { this.$refs.searchInput.focus(); }
          }
        });
      } else {
        document.removeEventListener("mousedown", this.onDocClick, true);
        document.removeEventListener("keydown", this.onDocKey, true);
        this.query = "";
        this.applyQueryNow();
      }
    },
    options() {
      this.defaultOpenSeed = 0;
    },
    query(next) {
      clearTimeout(this.queryTimer);
      // Clearing the box is applied at once: there is nothing to wait for, and
      // the full tree coming back should not lag behind an empty input.
      if (!next.trim()) {
        this.applyQueryNow();
        return;
      }
      this.queryTimer = setTimeout(() => {
        this.queryTimer = null;
        this.appliedQuery = this.query;
      }, SEARCH_DEBOUNCE_MS);
    },
  },
  beforeDestroy() {
    clearTimeout(this.queryTimer);
    document.removeEventListener("mousedown", this.onDocClick, true);
    document.removeEventListener("keydown", this.onDocKey, true);
  },
  methods: {
    applyQueryNow() {
      clearTimeout(this.queryTimer);
      this.queryTimer = null;
      this.appliedQuery = this.query;
    },
    togglePanel() { this.panelOpen = !this.panelOpen; },
    close() { this.panelOpen = false; },
    onDocClick(e) {
      const root = this.$refs.root;
      if (!root) return;
      if (!root.contains(e.target)) this.close();
    },
    onDocKey(e) {
      if (e.key === "Escape") this.close();
    },
    someChildMatches(node, q) {
      if (!node.children) return false;
      const test = (n) => {
        if ((n.label || "").toLowerCase().includes(q)) return true;
        if (n.children) return n.children.some(test);
        return false;
      };
      return node.children.some(test);
    },
    labelFor(id) {
      const entry = this.nodeIndex[id];
      return entry ? entry.node.label : id;
    },
    // Chip label optionally prefixed with the parent group's `chipPrefix`,
    // so shared leaf labels stay distinguishable once selected
    // (e.g. "BP: Submitted"). Tree rows keep the plain `label`.
    chipLabelFor(id) {
      const entry = this.nodeIndex[id];
      if (!entry) return id;
      const parent =
        entry.parentId != null ? this.nodeIndex[entry.parentId] : null;
      // A node's own chipPrefix wins over its parent's, so a single leaf can be
      // prefixed without prefixing every sibling in the branch.
      const prefix =
        entry.node.chipPrefix ||
        (parent && parent.node ? parent.node.chipPrefix : null);
      return prefix ? `${prefix}: ${entry.node.label}` : entry.node.label;
    },
    seedDefaultOpen() {
      if (this.defaultOpenSeed) return;
      this.defaultOpenSeed = 1;
      const open = new Set();
      const maxDepth = this.defaultExpandDepth;
      const walk = (nodes, depth) => {
        for (const node of nodes) {
          if (depth < maxDepth && node.children && node.children.length > 0) {
            open.add(node.id);
            walk(node.children, depth + 1);
          }
        }
      };
      walk(this.options, 0);
      // also auto-open any branches that contain a current selection
      for (const id of this.value) {
        let e = this.nodeIndex[id];
        while (e && e.parentId) {
          open.add(e.parentId);
          e = this.nodeIndex[e.parentId];
        }
      }
      this.openIds = open;
    },
    onRowClick(row) {
      if (row.hasKids) this.toggleOpen(row.node);
      else this.onToggleSel(row.node);
    },
    // A node flagged disabled is never selectable, leaf or branch. A disabled
    // branch can still be opened (see flatRows); what it can't do is put its
    // leaves into the selection.
    //
    // A node flagged `locked` can't be toggled ON ITS OWN either, but unlike a
    // disabled leaf it is still taken along when its branch is toggled. That is
    // the parent's own cap on single picks (e.g. "up to five neighbourhoods")
    // where ticking the whole branch is still allowed.
    isDisabled(node) {
      return !!(node && (node.disabled || node.locked)) || this.capBlocks(node);
    },
    // True when the cap is full AND this node would only ever ADD to the
    // selection. A node with something already selected under it stays live,
    // because clicking it takes leaves OUT, which is the one move a full
    // control has to keep offering.
    capBlocks(node) {
      if (!this.atCap || !node) return false;
      const entry = this.nodeIndex[node.id];
      if (!entry) return false;
      return !entry.leafIds.some((id) => this.leafSelected.has(id));
    },
    // The leaf ids under an entry, minus any flagged disabled — so branch
    // toggles and branch checked-state ignore inert leaves.
    enabledLeafIds(entry) {
      return entry.leafIds.filter((id) => {
        const e = this.nodeIndex[id];
        return !(e && e.node && e.node.disabled);
      });
    },
    toggleOpen(node) {
      const next = new Set(this.openIds);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      this.openIds = next;
    },
    onToggleSel(node) {
      if (this.isDisabled(node)) return;
      const entry = this.nodeIndex[node.id];
      if (!entry) return;
      // Toggling a branch selects/clears only its enabled leaves — disabled
      // leaves (e.g. the opposite segment's statuses) are never picked up.
      const ids = this.enabledLeafIds(entry);
      if (!ids.length) return;
      const next = new Set(this.leafSelected);
      const allOn = ids.length > 0 && ids.every((id) => next.has(id));
      if (allOn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      this.emitFromLeafSet(next);
    },
    removeId(id) {
      const entry = this.nodeIndex[id];
      const next = new Set(this.leafSelected);
      if (entry) {
        entry.leafIds.forEach((lid) => next.delete(lid));
      } else {
        next.delete(id);
      }
      this.emitFromLeafSet(next);
    },
    clearAll() {
      this.emitFromLeafSet(new Set());
    },
    emitFromLeafSet(leafSet) {
      // THE CAP IS ENFORCED HERE AS WELL AS IN THE ROWS, because a branch
      // toggle can add many leaves in one click and the inert-row rule only
      // stops a branch that is entirely off. Trimmed in TREE ORDER, so which
      // leaves survive is the order the reader is looking at rather than the
      // order a Set happens to iterate in.
      if (this.capped && leafSet.size > this.maxLeaves) {
        const kept = new Set();
        const walk = (nodes) => {
          for (const node of nodes) {
            if (kept.size >= this.maxLeaves) return;
            if (node.children && node.children.length) walk(node.children);
            else if (leafSet.has(node.id)) kept.add(node.id);
          }
        };
        walk(this.options);
        leafSet = kept;
      }
      const out = [];
      const mode = this.valueConsistsOf;
      const walk = (nodes) => {
        for (const node of nodes) {
          const entry = this.nodeIndex[node.id];
          if (!entry) continue;
          const ids = entry.leafIds;
          let on = 0;
          for (const id of ids) if (leafSet.has(id)) on++;
          if (on === 0) continue;

          if (mode === "LEAF_PRIORITY") {
            if (entry.isLeaf) {
              if (leafSet.has(node.id)) out.push(node.id);
            } else {
              walk(node.children);
            }
            continue;
          }

          if (mode === "ALL") {
            if (on === ids.length) out.push(node.id);
            if (!entry.isLeaf) walk(node.children);
            else if (leafSet.has(node.id) && !out.includes(node.id)) out.push(node.id);
            continue;
          }

          // BRANCH_PRIORITY (default)
          if (on === ids.length) {
            out.push(node.id);
            continue;
          }
          if (entry.isLeaf) {
            if (leafSet.has(node.id)) out.push(node.id);
          } else {
            walk(node.children);
          }
        }
      };
      walk(this.options);
      this.$emit("input", out);
    },
  },
};
</script>

<style scoped>
.ast {
  /* MarketStrata navy palette */
  --ast-accent: #0e1b2c;
  --ast-accent-ring: rgba(14, 27, 44, 0.18);
  --ast-accent-hover: #152030;
  --ast-accent-tint: #eff6ff;
  --ast-accent-text: #1d4ed8;
  --ast-surface: #ffffff;
  --ast-surface-sunk: #f8f9fa;
  --ast-fg-1: #111827;
  --ast-fg-2: #374151;
  --ast-fg-3: #9ca3af;
  --ast-border: #e8eaed;
  --ast-border-soft: #e9e8e9;
  --ast-border-strong: #d1d5db;
  --ast-divider: #eef0f4;
  --ast-font: var(--rpd-font-sans, "Inter Tight", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
  --ast-font-display: var(--rpd-font-sans, "Inter Tight", "Inter", sans-serif);
  position: relative;
  display: block;
  width: 100%;
  font-family: var(--ast-font);
  color: var(--ast-fg-1);
}

/* ── Trigger ─────────────────────────────────────────── */
.ast-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 32px;
  padding: 3px 6px 3px 8px;
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 6px;
  font: inherit;
  font-size: 12px;
  color: var(--ast-fg-1);
  cursor: pointer;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}

.ast-trigger:hover {
  border-color: #c8ccd3;
}

.ast--open .ast-trigger {
  border-color: var(--ast-accent);
  box-shadow: 0 0 0 3px var(--ast-accent-ring);
}

.ast-trigger:focus-visible {
  outline: none;
  border-color: var(--ast-accent);
  box-shadow: 0 0 0 3px var(--ast-accent-ring);
}

.ast-trigger-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  overflow: hidden;
  min-width: 0;
}

.ast-trigger-placeholder {
  font-size: 12px;
  color: #9ca3af;
  letter-spacing: 0;
  font-weight: 400;
  white-space: nowrap;
}

.ast-trigger-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--ast-accent);
  color: #fff;
  font-size: 11px;
  line-height: 1.1;
  padding: 2px 4px 2px 7px;
  border-radius: 4px;
  max-width: 130px;
}

.ast-trigger-chip-label {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ast-trigger-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}

.ast-trigger-chip-x:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.ast-trigger-more {
  font-size: 11px;
  font-weight: 500;
  color: var(--ast-fg-2);
  padding: 1px 4px;
  border-radius: 4px;
  background: #eef1f6;
  white-space: nowrap;
}

.ast-trigger-tail {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.ast-trigger-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  color: var(--ast-fg-3);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}

.ast-trigger-clear:hover {
  color: var(--ast-fg-1);
  background: #eef1f6;
}

.ast-trigger-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--ast-fg-3);
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1), color 160ms ease;
}

.ast-trigger-caret--open {
  transform: rotate(-180deg);
  color: var(--ast-accent);
}

/* ── Panel ───────────────────────────────────────────── */
.ast-panel {
  position: absolute;
  top: calc(100% + 6px);
  z-index: 1500;
  width: 380px;
  max-width: calc(100vw - 32px);
  background: var(--ast-surface);
  border: 1px solid var(--ast-border);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(18, 40, 63, 0.1), 0 16px 36px -8px rgba(18, 40, 63, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--ast-fg-1);
}

.ast-panel--left {
  left: 0;
}

.ast-panel--right {
  right: 0;
}

.ast-pop-enter-active,
.ast-pop-leave-active {
  transition: opacity 160ms ease, transform 200ms cubic-bezier(0.2, 0, 0, 1);
}

.ast-pop-enter,
.ast-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.99);
}

/* Field */
.ast-field {
  padding: 12px 12px 11px;
  border-bottom: 1px solid var(--ast-divider);
}

.ast-fieldlabel {
  font-family: var(--ast-font-display);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ast-accent);
  margin: 0 2px 7px;
}

.ast-inputwrap {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  min-height: 38px;
  border: 1.5px solid var(--ast-accent);
  border-radius: 8px;
  padding: 5px 8px;
  background: #fff;
  box-shadow: 0 0 0 3px var(--ast-accent-ring);
}

.ast-pchip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--ast-accent);
  color: #fff;
  border: 1px solid transparent;
  font-size: 12.5px;
  font-weight: 500;
  padding: 3px 6px 3px 9px;
  border-radius: 4px;
  line-height: 1.1;
}

.ast-pchip b {
  font-weight: 500;
}

.ast-pchip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 5px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: background 120ms ease, color 120ms ease;
}

.ast-pchip-x:hover {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.ast-pchip-more {
  font-size: 12px;
  font-weight: 500;
  color: var(--ast-fg-2);
  padding: 2px 4px;
}

.ast-search {
  flex: 1;
  min-width: 70px;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  color: var(--ast-fg-1);
  background: transparent;
  padding: 3px 2px;
}

.ast-search::placeholder {
  color: var(--ast-fg-3);
}

/* Body */
.ast-body {
  overflow-y: auto;
  padding: 6px 4px 8px;
  scrollbar-width: thin;
  scrollbar-color: var(--ast-border-strong) transparent;
}

.ast-body::-webkit-scrollbar {
  width: 9px;
}

.ast-body::-webkit-scrollbar-thumb {
  background: var(--ast-border-strong);
  border-radius: 9px;
  border: 2px solid #fff;
}

.ast-body::-webkit-scrollbar-track {
  background: transparent;
}

/* Row */
.ast-row {
  display: flex;
  align-items: center;
  height: 34px;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  user-select: none;
  padding-right: 8px;
  transition: background 110ms ease;
}

.ast-row:hover {
  background: var(--ast-surface-sunk);
}

.ast-row.ast-row--sel {
  background: var(--ast-accent-tint);
}

.ast-row.ast-row--sel:hover {
  background: #dbeafe;
}

/* Disabled row — greyed out (e.g. a status not valid for the currently
   selected segment, or a category a role may not filter on). A disabled
   branch keeps its click target so it can still be expanded; only a disabled
   leaf is inert, since there is nothing under it left to reveal. */
.ast-row.ast-row--disabled {
  opacity: 0.42;
}

.ast-row.ast-row--inert {
  cursor: not-allowed;
  pointer-events: none;
}

.ast-row.ast-row--disabled .ast-cb {
  cursor: not-allowed;
}

/* indent guides */
.ast-guides {
  display: flex;
  align-self: stretch;
  flex: 0 0 auto;
}

.ast-guide {
  width: 21px;
  align-self: stretch;
  position: relative;
}

.ast-guide::before {
  content: "";
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 1.5px;
  background: var(--ast-divider);
}

.ast-chev {
  width: 22px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: var(--ast-fg-3);
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1), color 140ms ease;
}

.ast-chev--open {
  transform: rotate(90deg);
  color: var(--ast-fg-2);
}

.ast-chev--empty {
  opacity: 0;
}

.ast-cb {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.6px solid var(--ast-border-strong);
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 9px;
  background: #fff;
  color: #fff;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease, transform 140ms ease;
}

.ast-row:hover .ast-cb {
  border-color: var(--ast-accent);
}

.ast-cb--on,
.ast-cb--ind {
  background: var(--ast-accent);
  border-color: var(--ast-accent);
}

.ast-cb-dash {
  width: 9px;
  height: 2px;
  background: #fff;
  border-radius: 2px;
}

.ast-name {
  flex: 1;
  font-size: 13.5px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ast-row--l0 .ast-name {
  font-family: var(--ast-font-display);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: -0.005em;
}

.ast-row--l1 .ast-name {
  font-weight: 600;
  font-size: 13.5px;
}

.ast-row--l2 .ast-name {
  font-weight: 500;
}

.ast-row--l3 .ast-name {
  font-weight: 400;
  color: var(--ast-fg-2);
}

.ast-count {
  font-size: 11.5px;
  font-weight: 500;
  color: var(--ast-fg-3);
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
  margin-left: 8px;
}

.ast-row--sel .ast-count {
  color: var(--ast-accent-text);
}

.ast-row--sel .ast-name {
  color: var(--ast-accent-text);
  font-weight: 500;
}

.ast-empty {
  padding: 26px 16px;
  text-align: center;
  color: var(--ast-fg-3);
  font-size: 13px;
}

/* Footer */
.ast-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--ast-divider);
  background: var(--ast-surface-sunk);
}

.ast-summary {
  font-size: 13px;
  color: var(--ast-fg-2);
}

.ast-summary b {
  color: var(--ast-fg-1);
  font-weight: 600;
}

.ast-actions {
  display: flex;
  gap: 7px;
}

.ast-btn {
  font-family: var(--ast-font-display);
  font-size: 12.5px;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 13px;
  cursor: pointer;
  border: 1px solid var(--ast-border);
  background: #fff;
  color: var(--ast-fg-2);
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease, opacity 120ms ease;
}

.ast-btn:hover:not(:disabled) {
  background: var(--ast-surface-sunk);
  color: var(--ast-fg-1);
}

.ast-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ast-btn--pri {
  background: var(--ast-accent);
  border-color: var(--ast-accent);
  color: #fff;
}

.ast-btn--pri:hover:not(:disabled) {
  background: var(--ast-accent-hover);
  border-color: var(--ast-accent-hover);
  color: #fff;
}

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 540px) {
  .ast-panel {
    position: fixed;
    top: auto;
    left: 16px;
    right: 16px;
    bottom: 16px;
    width: auto;
    max-width: none;
    max-height: 75vh;
    z-index: 2000;
  }

  .ast-body {
    flex: 1;
    max-height: none !important;
  }
}
</style>

<style>
/* ── Dark mode — mirrors MarketStrata's dark palette ── */
html.dark .ast {
  --ast-accent: #4db7fe;
  --ast-accent-hover: #6cc4ff;
  --ast-accent-ring: rgba(77, 183, 254, 0.24);
  --ast-accent-tint: rgba(77, 183, 254, 0.12);
  --ast-accent-text: #4db7fe;
  --ast-surface: #152030;
  --ast-surface-sunk: #0e1b2c;
  --ast-fg-1: #d6e4f0;
  --ast-fg-2: rgba(255, 255, 255, 0.65);
  --ast-fg-3: rgba(255, 255, 255, 0.4);
  --ast-border: rgba(255, 255, 255, 0.1);
  --ast-border-soft: rgba(255, 255, 255, 0.07);
  --ast-border-strong: rgba(255, 255, 255, 0.28);
  --ast-divider: rgba(255, 255, 255, 0.08);
}

html.dark .ast-trigger {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--ast-fg-1);
}

html.dark .ast-trigger:hover {
  border-color: rgba(255, 255, 255, 0.22);
}

html.dark .ast-trigger-placeholder {
  color: rgba(255, 255, 255, 0.36);
}

html.dark .ast-trigger-chip {
  background: #0e1b2c;
  color: #d6e4f0;
}

html.dark .ast-trigger-chip-x {
  color: rgba(255, 255, 255, 0.55);
}

html.dark .ast-trigger-chip-x:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

html.dark .ast-trigger-more {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.72);
}

html.dark .ast-trigger-clear:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

html.dark .ast-panel {
  background: var(--ast-surface);
  border-color: var(--ast-border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 16px 36px -8px rgba(0, 0, 0, 0.55);
}

html.dark .ast-inputwrap {
  background: #0e1b2c;
}

html.dark .ast-pchip {
  background: #0e1b2c;
  color: #d6e4f0;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .ast-search {
  color: var(--ast-fg-1);
}

html.dark .ast-search::placeholder {
  color: rgba(255, 255, 255, 0.42);
}

html.dark .ast-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
  border-color: var(--ast-surface);
}

html.dark .ast-cb {
  background: #1b2a3f;
  border-color: var(--ast-border-strong);
}

html.dark .ast-cb--on,
html.dark .ast-cb--ind {
  background: var(--ast-accent);
  border-color: var(--ast-accent);
  color: #0e1b2c;
}

html.dark .ast-cb-dash {
  background: #0e1b2c;
}

html.dark .ast-foot {
  background: rgba(0, 0, 0, 0.22);
  border-top-color: var(--ast-divider);
}

html.dark .ast-btn {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.78);
}

html.dark .ast-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

html.dark .ast-btn--pri {
  background: var(--ast-accent);
  border-color: var(--ast-accent);
  color: #0e1b2c;
}

html.dark .ast-btn--pri:hover:not(:disabled) {
  background: var(--ast-accent-hover);
  border-color: var(--ast-accent-hover);
  color: #0e1b2c;
}

html.dark .ast-row:hover {
  background: rgba(255, 255, 255, 0.04);
}

html.dark .ast-row.ast-row--sel {
  background: rgba(77, 183, 254, 0.1);
}

html.dark .ast-row.ast-row--sel:hover {
  background: rgba(77, 183, 254, 0.18);
}

html.dark .ast-row--sel .ast-name {
  color: var(--ast-accent-text);
}

html.dark .ast-row--sel .ast-count {
  color: var(--ast-accent-text);
}

html.dark .ast-fieldlabel {
  color: var(--ast-accent);
}
</style>
